import { useState, useCallback } from 'react';

export const useSelection = (bookContentRef) => {
  const [selection, setSelection] = useState({
    startWord: null,
    endWord: null,
    text: null,
    mode: null // 'mouse' or 'tap'
  });

  const clearSelection = useCallback(() => {
    const spans = bookContentRef.current?.querySelectorAll(".note-preview");
    spans?.forEach((span) => span.classList.remove("note-preview"));
    window.getSelection().removeAllRanges();
    setSelection({
      startWord: null,
      endWord: null,
      text: null,
      mode: null
    });
  }, [bookContentRef]);

  const applyPreview = useCallback((startWord, endWord) => {
    clearSelection();
    
    const start = parseInt(startWord);
    const end = parseInt(endWord);
    const minWord = Math.min(start, end);
    const maxWord = Math.max(start, end);
    
    const selectedSpans = [];
    
    for (let i = minWord; i <= maxWord; i++) {
      const span = bookContentRef.current?.querySelector(`[data-w="${i}"]`);
      if (span) {
        span.classList.add("note-preview");
        selectedSpans.push(span.textContent);
      }
    }
    
    return selectedSpans.join(' ').trim();
  }, [bookContentRef, clearSelection]);

  const handleWordClick = useCallback((e) => {
    const wordElement = e.target.closest('[data-w]');
    if (!wordElement) return;
    
    const wordIndex = wordElement.dataset.w;
    
    if (selection.mode === 'tap' && selection.startWord) {
      // Complete selection
      const text = applyPreview(selection.startWord, wordIndex);
      setSelection({
        startWord: selection.startWord,
        endWord: wordIndex,
        text: text,
        mode: 'tap'
      });
    } else {
      // Start new selection
      const text = applyPreview(wordIndex, wordIndex);
      setSelection({
        startWord: wordIndex,
        endWord: wordIndex,
        text: text,
        mode: 'tap'
      });
    }
  }, [selection, applyPreview]);

  const handleMouseUp = useCallback((e) => {
    const windowSelection = window.getSelection();
    
    if (windowSelection.rangeCount === 0 || windowSelection.isCollapsed) {
      handleWordClick(e);
      return;
    }
    
    const range = windowSelection.getRangeAt(0);
    const startElement = range.startContainer.parentElement?.closest('[data-w]');
    const endElement = range.endContainer.parentElement?.closest('[data-w]');
    
    if (startElement && endElement) {
      const startWord = startElement.dataset.w;
      const endWord = endElement.dataset.w;
      const text = applyPreview(startWord, endWord);
      
      setSelection({
        startWord: startWord,
        endWord: endWord,
        text: text,
        mode: 'mouse'
      });
    }
    
    window.getSelection().removeAllRanges();
  }, [handleWordClick, applyPreview]);

  return {
    selection,
    setSelection,
    clearSelection,
    handleWordClick,
    handleMouseUp
  };
};

